/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, Input, Output, EventEmitter, OnInit, Injector, Optional, NgZone, forwardRef } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { A11yModule } from '@angular/cdk/a11y';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { BackToCloseModalDirective } from '../../directives/back-to-close-modal.directive';
import { NvFocusRippleDirective } from '../../directives/focus-ripple.directive';
import { HyperLinkUserActivationGuardDirective } from '../../directives/hyperlink-user-activation-guard.directive';
import { NvAddKeyboardActivateDirective } from '../../directives/nv-mat-keyboard-fixups.directive';
import {
    NvMatRadioButtonSpatialNavigationFixupDirective,
    CheckboxFocusMonitorDirective
} from '../../directives/nv-mat-spatial-navigation-fixups.directive';
import { OffDeviceLinkDirective } from '../../directives/off-device-link.directive';
import { SpatialNavigationContainerDirective } from '../../directives/spatial-navigation-container.directive';
import { TrapFocusDirective } from '../../directives/trap-focus.directive';
import { NvHigTableColumns } from '../../models/hig-modal-shared.types';
import {
    NvGenericDialogConfig,
    NvGenericDialogData,
    NvHigDialogRef,
    addPanelClass
} from '../nv-generic-dialog/nv-generic-dialog.types';
import { NvHigCellComparisonComponent } from '../nv-hig-cell-upgrade/nv-hig-cell_comparison.component';
import { NvHigTableComparisonComponent } from '../nv-hig-generic-table/nv-hig-table_comparison.component';
import { NvHigListMetadataComponent } from '../nv-hig-list-metadata/nv-hig-list-metadata.component';

@Component({
    selector: 'nv-hig-dialog',
    templateUrl: './nv-hig-dialog.component.html',
    styleUrls: ['./nv-hig-dialog.component.scss'],
    imports: [
        NgClass,
        NgTemplateOutlet,
        FormsModule,
        FlexLayoutModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        MatRadioModule,
        MatProgressSpinnerModule,
        TranslateModule,
        A11yModule,
        RouterModule,
        SpatialNavigationContainerDirective,
        TrapFocusDirective,
        NvMatRadioButtonSpatialNavigationFixupDirective,
        CheckboxFocusMonitorDirective,
        NvAddKeyboardActivateDirective,
        AutoFocusDirective,
        forwardRef(() => OffDeviceLinkDirective),
        HyperLinkUserActivationGuardDirective,
        NvFocusRippleDirective,
        NvHigCellComparisonComponent,
        NvHigTableComparisonComponent,
        NvHigListMetadataComponent
    ]
})
export class NvHigDialogComponent extends BackToCloseModalDirective implements OnInit {
    @Output() primaryButtonClick = new EventEmitter<MouseEvent>();
    @Output() secondaryButtonClick = new EventEmitter<MouseEvent>();
    @Output() radioButtonClick = new EventEmitter();
    @Output() linkClick = new EventEmitter();
    @Output() tableCTAClick = new EventEmitter<{ column: NvHigTableColumns }>();
    @Input() data: NvGenericDialogData = {};

    get hasIcon(): boolean {
        return !!this.data.icon || !!this.data.image;
    }

    get autoFocusOnPrimaryButton(): boolean {
        return this.data.autoFocusOnPrimaryButton ?? true;
    }

    get shouldShowLinkRegion(): boolean {
        const link = this.data.link;
        return (
            !!link && !link.placeholders && !!link.text && !!(link.url || link.routerLink || link.linkClickedCallback)
        );
    }

    static open(matDialog: MatDialog, config: NvGenericDialogConfig): NvHigDialogRef {
        addPanelClass(config, { addOverlayPanelCls: true });
        return matDialog.open(NvHigDialogComponent, config);
    }

    constructor(
        injector: Injector,
        @Optional() dialogRef: MatDialogRef<any>,
        @Optional() bottomSheetRef: MatBottomSheetRef<any>,
        ngZone: NgZone
    ) {
        super(dialogRef, bottomSheetRef, injector, ngZone);
        this.data = injector.get(MAT_DIALOG_DATA, null) || { showSpinner: false };
    }

    ngOnInit(): void {
        this.data.checkboxState = this.data.checkbox?.initialState ?? false;
    }

    onPrimaryButtonClick(event: MouseEvent) {
        this.primaryButtonClick.emit(event);
    }

    onSecondaryButtonClick(event: MouseEvent) {
        this.secondaryButtonClick.emit(event);
    }

    onLinkClick() {
        this.linkClick.emit();
    }

    onRadioOptionChange() {
        this.radioButtonClick.emit();
    }

    onImageDragStart(): void {
        this.data.image?.dragStartCallback();
    }

    onTableCTAClick(event: { column: NvHigTableColumns }): void {
        this.tableCTAClick.emit(event);
    }
}
